/**
 * AdminBackgroundRetention — Compliance Ops panel.
 *
 * Wires the retention + deletion-queue + activity-alerts backend
 * endpoints (see routers/bg_retention.py) into a single admin
 * surface so Compliance/Platform admins can:
 *   • See the configured retention window + last 10 sweep runs.
 *   • Trigger a dry-run to preview what would be purged.
 *   • Trigger a real purge (with a confirm gate).
 *   • Review candidate deletion requests, approve/reject them,
 *     and apply per-check legal holds.
 *   • See red-flag activity alerts (stuck in-progress, stale
 *     disputes, adverse-action windows closing soon).
 *
 * Access: platform_admin + compliance_admin only. Support tier is
 * intentionally locked out of this surface because deletions
 * cannot be undone.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    Loader2, RefreshCw, Trash2, ShieldAlert, ShieldCheck, PlayCircle,
    Clock, Flag, AlertTriangle, Check, X, Lock,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const AlertTile = ({ icon: Icon, count, label, tone = "amber", testId }) => {
    const toneMap = {
        amber: "border-amber-500/40 bg-amber-500/[0.06] text-amber-800 dark:text-amber-300",
        rose: "border-rose-500/40 bg-rose-500/[0.06] text-rose-800 dark:text-rose-300",
        sky: "border-sky-500/40 bg-sky-500/[0.06] text-sky-800 dark:text-sky-300",
        emerald: "border-emerald-500/40 bg-emerald-500/[0.06] text-emerald-800 dark:text-emerald-300",
    };
    return (
        <div className={`rounded-xl border p-3 ${toneMap[tone]}`} data-testid={testId}>
            <div className="inline-flex items-center gap-1.5 text-xs font-semibold">
                <Icon size={12} /> {label}
            </div>
            <div className="mt-1 font-heading text-2xl font-semibold text-foreground">{count ?? 0}</div>
        </div>
    );
};

export default function AdminBackgroundRetention() {
    const [status, setStatus] = useState(null);
    const [alerts, setAlerts] = useState(null);
    const [queue, setQueue] = useState([]);
    const [loading, setLoading] = useState(true);
    const [runBusy, setRunBusy] = useState(false);
    const [confirmPurgeOpen, setConfirmPurgeOpen] = useState(false);
    const [confirmPurgeText, setConfirmPurgeText] = useState("");
    const [confirmActionState, setConfirmActionState] = useState(null); // {req, action, notes}

    const loadAll = useCallback(async () => {
        setLoading(true);
        try {
            const [rStatus, rAlerts, rQueue] = await Promise.all([
                api.get("/admin/background-checks/retention"),
                api.get("/admin/background-checks/alerts"),
                api.get("/admin/background-checks/deletion-queue"),
            ]);
            setStatus(rStatus.data);
            setAlerts(rAlerts.data);
            setQueue(rQueue.data?.results || []);
        } catch (err) {
            const msg = err?.response?.data?.detail || "Failed to load compliance ops data.";
            toast.error(msg);
            if (err?.response?.status === 403) {
                setStatus({ forbidden: true });
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadAll(); }, [loadAll]);

    const runSweep = async (dryRun = true) => {
        setRunBusy(true);
        try {
            const { data } = await api.post(
                `/admin/background-checks/retention/run?dry_run=${dryRun ? "true" : "false"}`,
            );
            toast.success(
                dryRun
                    ? `Dry-run: would purge ${data.purged}, hold ${data.held} of ${data.scanned} scanned.`
                    : `Purge complete: ${data.purged} purged, ${data.held} held.`,
            );
            await loadAll();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Retention sweep failed.");
        } finally {
            setRunBusy(false);
            setConfirmPurgeOpen(false);
            setConfirmPurgeText("");
        }
    };

    const actionQueue = async (req, action, notes = "") => {
        try {
            const { data } = await api.patch(
                `/admin/background-checks/deletion-queue/${req.id}`,
                { action, notes: notes || undefined },
            );
            toast.success(
                action === "approve"
                    ? `Approved — ${data.request?.purged_check_ids?.length || 0} check(s) purged.`
                    : action === "reject"
                        ? "Deletion request rejected."
                        : "Marked for review.",
            );
            setConfirmActionState(null);
            await loadAll();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Action failed.");
        }
    };

    const canOperate = useMemo(() => !!status && !status.forbidden, [status]);

    if (loading) {
        return (
            <div className="container-app py-12" data-testid="admin-retention-loading">
                <div className="inline-flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 size={14} className="animate-spin" /> Loading compliance ops…
                </div>
            </div>
        );
    }

    if (status?.forbidden) {
        return (
            <div className="container-app py-12" data-testid="admin-retention-forbidden">
                <div className="rounded-2xl border border-rose-500/40 bg-rose-500/5 p-6">
                    <div className="inline-flex items-center gap-2 font-semibold text-rose-700 dark:text-rose-400">
                        <Lock size={14} /> Compliance role required
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Retention operations are limited to Compliance/Platform admins. Contact a Compliance admin if you need
                        to trigger a sweep or action a deletion request.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="container-app py-10" data-testid="admin-retention-page">
            <header className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <p className="overline inline-flex items-center gap-1"><ShieldCheck size={11} /> Compliance ops</p>
                    <h1 className="mt-2 font-heading font-semibold text-3xl tracking-tight text-foreground">
                        Background-check retention
                    </h1>
                    <p className="mt-1 text-sm text-muted-foreground max-w-2xl">
                        Configured window, sweep history, candidate deletion queue, and red-flag activity alerts.
                        Sweeps skip any check with an active adverse-action, open dispute, or legal hold.
                    </p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <button onClick={loadAll} className="btn-outline text-xs inline-flex items-center gap-1.5" data-testid="ret-refresh">
                        <RefreshCw size={12} /> Refresh
                    </button>
                    <Link to="/admin/background-checks" className="btn-outline text-xs inline-flex items-center gap-1.5" data-testid="ret-bg-link">
                        <ShieldCheck size={12} /> All background checks
                    </Link>
                </div>
            </header>

            {/* Alerts row */}
            <section className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="ret-alerts">
                <AlertTile icon={Clock} tone="amber"
                    count={alerts?.stuck_in_progress_over_7d}
                    label={`Stuck > ${alerts?.thresholds?.stuck_in_progress_days || 7}d`}
                    testId="ret-alert-stuck" />
                <AlertTile icon={Flag} tone="rose"
                    count={alerts?.stale_disputes_over_30d}
                    label={`Stale disputes > ${alerts?.thresholds?.dispute_stale_days || 30}d`}
                    testId="ret-alert-disputes" />
                <AlertTile icon={AlertTriangle} tone="amber"
                    count={alerts?.adverse_waiting_closing_soon}
                    label={`Adverse window ≤ ${alerts?.thresholds?.adverse_closing_business_days || 2} biz days`}
                    testId="ret-alert-adverse" />
                <AlertTile icon={Trash2} tone="sky"
                    count={alerts?.open_deletion_requests}
                    label="Open deletion requests"
                    testId="ret-alert-deletions" />
            </section>

            {/* Retention config + run controls */}
            <section className="mt-6 card-soft p-5" data-testid="ret-config">
                <div className="flex items-start justify-between flex-wrap gap-3">
                    <div>
                        <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-foreground">
                            <ShieldAlert size={12} className="text-emerald-600" /> Retention window
                        </div>
                        <div className="mt-1 text-3xl font-heading font-semibold text-foreground" data-testid="ret-window-days">
                            {status?.retention_days ?? "—"} <span className="text-sm font-normal text-muted-foreground">days</span>
                        </div>
                        <p className="mt-1 text-xs text-muted-foreground">
                            Cutoff preview: {fmtDate(status?.cutoff_preview)}. Adverse-action + dispute rows are exempt.
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <button
                            onClick={() => runSweep(true)}
                            disabled={!canOperate || runBusy}
                            className="btn-outline text-xs inline-flex items-center gap-1.5"
                            data-testid="ret-run-dry"
                        >
                            {runBusy ? <Loader2 size={12} className="animate-spin" /> : <PlayCircle size={12} />}
                            Dry-run sweep
                        </button>
                        <button
                            onClick={() => setConfirmPurgeOpen(true)}
                            disabled={!canOperate || runBusy}
                            className="inline-flex items-center gap-1.5 rounded-full bg-rose-600 hover:bg-rose-700 text-white px-3 py-1.5 text-xs font-semibold disabled:opacity-60"
                            data-testid="ret-run-real"
                        >
                            <Trash2 size={12} /> Run purge now
                        </button>
                    </div>
                </div>
            </section>

            {/* Recent runs */}
            <section className="mt-6 card-soft p-5" data-testid="ret-runs">
                <h2 className="font-heading font-semibold text-lg text-foreground">Recent sweep runs</h2>
                {(!status?.recent_runs || status.recent_runs.length === 0) ? (
                    <p className="mt-2 text-sm text-muted-foreground">No sweep has been run yet.</p>
                ) : (
                    <div className="mt-3 overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="text-xs text-muted-foreground border-b border-border">
                                <tr>
                                    <th className="text-left py-2 pr-3">When</th>
                                    <th className="text-left py-2 pr-3">Mode</th>
                                    <th className="text-right py-2 pr-3">Scanned</th>
                                    <th className="text-right py-2 pr-3">Purged</th>
                                    <th className="text-right py-2 pr-3">Held</th>
                                    <th className="text-left py-2 pr-3">Hold reasons</th>
                                </tr>
                            </thead>
                            <tbody>
                                {status.recent_runs.map((r, i) => (
                                    <tr key={r.id || i} className="border-b border-border last:border-b-0">
                                        <td className="py-2 pr-3 text-foreground">{fmtDate(r.at)}</td>
                                        <td className="py-2 pr-3">
                                            <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${
                                                r.dry_run ? "border-sky-500/40 bg-sky-500/10 text-sky-700 dark:text-sky-400"
                                                          : "border-rose-500/40 bg-rose-500/10 text-rose-700 dark:text-rose-400"
                                            }`}>
                                                {r.dry_run ? "Dry-run" : "Purge"}
                                            </span>
                                            {r.truncated && (
                                                <span
                                                    data-testid={`ret-run-truncated-${i}`}
                                                    className="ml-1 inline-flex items-center rounded-full border border-rose-500/40 bg-rose-500/15 px-2 py-0.5 text-[10px] font-semibold text-rose-700 dark:text-rose-400"
                                                    title="Sweep hit the BG_RETENTION_PURGE_MAX ceiling — schedule a follow-up run."
                                                >
                                                    Truncated
                                                </span>
                                            )}
                                        </td>
                                        <td className="py-2 pr-3 text-right font-mono">{r.scanned ?? 0}</td>
                                        <td className="py-2 pr-3 text-right font-mono text-rose-700 dark:text-rose-400">{r.purged ?? 0}</td>
                                        <td className="py-2 pr-3 text-right font-mono text-amber-700 dark:text-amber-400">{r.held ?? 0}</td>
                                        <td className="py-2 pr-3 text-[11px] text-muted-foreground">
                                            {r.hold_reasons && Object.keys(r.hold_reasons).length > 0
                                                ? Object.entries(r.hold_reasons).map(([k, v]) => `${k}: ${v}`).join(" · ")
                                                : "—"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            {/* Deletion queue */}
            <section className="mt-6 card-soft p-5" data-testid="ret-queue">
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <h2 className="font-heading font-semibold text-lg text-foreground">Candidate deletion queue</h2>
                    <span className="text-xs text-muted-foreground">{queue.length} record(s)</span>
                </div>

                {queue.length === 0 ? (
                    <p className="mt-2 text-sm text-muted-foreground">No candidate deletion requests are open.</p>
                ) : (
                    <div className="mt-3 space-y-2">
                        {queue.map((row) => (
                            <div key={row.id} className="rounded-lg border border-border p-3" data-testid={`ret-queue-row-${row.id}`}>
                                <div className="flex items-start justify-between flex-wrap gap-2">
                                    <div className="min-w-0">
                                        <div className="inline-flex items-center gap-2 flex-wrap">
                                            <span className="font-mono text-xs text-muted-foreground">{row.id}</span>
                                            <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase ${
                                                row.status === "open" ? "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400"
                                                : row.status === "completed" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                                                : row.status === "rejected" ? "border-rose-500/40 bg-rose-500/10 text-rose-700 dark:text-rose-400"
                                                : "border-slate-500/40 bg-slate-500/10 text-slate-700 dark:text-slate-400"
                                            }`}>
                                                {row.status}
                                            </span>
                                        </div>
                                        <div className="mt-1 text-sm font-semibold text-foreground">{row.candidate_email}</div>
                                        <div className="text-xs text-muted-foreground">
                                            Opened {fmtDate(row.created_at)} · {row.check_ids?.length || 0} check(s)
                                        </div>
                                        {row.reason && (
                                            <p className="mt-1 text-xs text-muted-foreground italic">&ldquo;{row.reason}&rdquo;</p>
                                        )}
                                    </div>
                                    {row.status === "open" && (
                                        <div className="flex flex-wrap gap-1.5">
                                            <button
                                                onClick={() => setConfirmActionState({ req: row, action: "approve", notes: "" })}
                                                className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 px-2.5 py-1 text-[11px] font-semibold hover:bg-emerald-500/15"
                                                data-testid={`ret-queue-approve-${row.id}`}
                                            >
                                                <Check size={11} /> Approve &amp; purge
                                            </button>
                                            <button
                                                onClick={() => setConfirmActionState({ req: row, action: "reject", notes: "" })}
                                                className="inline-flex items-center gap-1 rounded-full border border-rose-500/40 bg-rose-500/10 text-rose-700 dark:text-rose-400 px-2.5 py-1 text-[11px] font-semibold hover:bg-rose-500/15"
                                                data-testid={`ret-queue-reject-${row.id}`}
                                            >
                                                <X size={11} /> Reject
                                            </button>
                                        </div>
                                    )}
                                </div>
                                {row.admin_notes && (
                                    <p className="mt-2 text-[11px] text-muted-foreground">
                                        <strong className="text-foreground">Admin note:</strong> {row.admin_notes}
                                    </p>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {/* Confirm purge dialog */}
            <Dialog open={confirmPurgeOpen} onOpenChange={(v) => { setConfirmPurgeOpen(v); if (!v) setConfirmPurgeText(""); }}>
                <DialogContent data-testid="ret-confirm-purge">
                    <DialogHeader>
                        <DialogTitle className="inline-flex items-center gap-2">
                            <Trash2 size={14} className="text-rose-600" /> Run retention purge now?
                        </DialogTitle>
                        <DialogDescription>
                            This will scrub every background-check row past its {status?.retention_days ?? "?"}-day
                            retention window, except those with an active adverse-action, open dispute, or legal hold.
                            The scrub is irreversible.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2">
                        <label className="text-xs font-semibold text-foreground">
                            Type <span className="font-mono rounded bg-muted px-1 py-0.5">PURGE</span> to confirm
                        </label>
                        <input
                            type="text"
                            value={confirmPurgeText}
                            onChange={(e) => setConfirmPurgeText(e.target.value)}
                            data-testid="ret-confirm-purge-text"
                            autoComplete="off"
                            placeholder="PURGE"
                            className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-rose-500/40"
                        />
                        <p className="text-[11px] text-muted-foreground">
                            Case-sensitive. Typing anything else will keep the button disabled.
                        </p>
                    </div>
                    <DialogFooter className="gap-2">
                        <button onClick={() => { setConfirmPurgeOpen(false); setConfirmPurgeText(""); }} className="btn-outline text-xs" data-testid="ret-confirm-cancel">
                            Cancel
                        </button>
                        <button
                            onClick={() => runSweep(false)}
                            disabled={runBusy || confirmPurgeText !== "PURGE"}
                            className="inline-flex items-center gap-1 rounded-full bg-rose-600 hover:bg-rose-700 text-white px-3 py-1.5 text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                            data-testid="ret-confirm-run"
                        >
                            {runBusy ? <Loader2 size={11} className="animate-spin" /> : <Trash2 size={11} />}
                            Confirm — purge now
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Confirm queue action dialog */}
            <Dialog open={!!confirmActionState} onOpenChange={(v) => !v && setConfirmActionState(null)}>
                <DialogContent data-testid="ret-confirm-queue-action">
                    <DialogHeader>
                        <DialogTitle>
                            {confirmActionState?.action === "approve"
                                ? "Approve deletion & purge now?"
                                : "Reject deletion request?"}
                        </DialogTitle>
                        <DialogDescription>
                            {confirmActionState?.action === "approve"
                                ? "Every non-held check for this candidate will be scrubbed immediately."
                                : "The candidate will remain on record. Add a note explaining why."}
                        </DialogDescription>
                    </DialogHeader>
                    <Textarea
                        rows={3}
                        value={confirmActionState?.notes || ""}
                        onChange={(e) => setConfirmActionState((s) => ({ ...s, notes: e.target.value }))}
                        placeholder="Internal note (optional)…"
                        data-testid="ret-queue-action-notes"
                    />
                    <DialogFooter className="gap-2">
                        <button onClick={() => setConfirmActionState(null)} className="btn-outline text-xs" data-testid="ret-queue-action-cancel">
                            Cancel
                        </button>
                        <button
                            onClick={() => actionQueue(confirmActionState.req, confirmActionState.action, confirmActionState.notes)}
                            className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold text-white ${
                                confirmActionState?.action === "approve"
                                    ? "bg-emerald-600 hover:bg-emerald-700"
                                    : "bg-rose-600 hover:bg-rose-700"
                            }`}
                            data-testid="ret-queue-action-confirm"
                        >
                            {confirmActionState?.action === "approve" ? <Check size={11} /> : <X size={11} />}
                            Confirm
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
